/**
 * Звірка ПДВ-кредиту за період (2026-08-04, рішення власника).
 *
 * Одна таблиця: проведені приходи + аванси постачальникам-платникам ПДВ
 * ± привʼязана вхідна ПН (той самий base_document-звʼязок, що колонки
 * «ПН» у списках). Показує розриви кредиту ДО декларації. Регламентний
 * клас звіту → single-org dropdown (Reports UX pattern).
 *
 * 2026-08-22 (Норма #29): серверна пагінація/сортування (конверт
 * items/total) через InfiniteDocGrid; фільтр статусу і підсумки — сервер.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  taxInvoicesIncoming,
  type VatCreditReconRow,
  type VatCreditReconTotals,
} from '@/api/taxInvoices';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaultPeriod, useDefaults } from '@/store/defaults';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const fmt = (v: string | number | null | undefined) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type StatusFilter = '' | 'missing' | 'unregistered' | 'ok';

export default function VatCreditReconciliationPage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('tax.view');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Лише платники ПДВ — у неплатника кредиту немає.
  const vatOrgs = useMemo(() => {
    const vatIds = new Set(
      (orgListQ.data?.items ?? []).filter((o) => o.is_vat_payer).map((o) => o.id));
    return availableOrgs.filter((o) => vatIds.has(o.id));
  }, [availableOrgs, orgListQ.data]);

  const [orgId, setOrgId] = useState('');
  const effectiveOrgId = orgId
    || (vatOrgs.find((o) => o.id === defaultOrgId)?.id ?? vatOrgs[0]?.id ?? '');
  // Стандарт фільтрів (2026-08-07, зауваження власника — як у ПН-списках):
  // локальні дати ПОРОЖНІ → діє глобальний «період за замовчуванням» з
  // шапки; звіт вимагає меж → без обох джерел падаємо на поточний місяць.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const gPeriod = useDefaultPeriod();
  const monthStart = () => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  };
  const effFrom = dateFrom || gPeriod.dateFrom?.slice(0, 10) || monthStart();
  const effTo = dateTo || gPeriod.dateTo?.slice(0, 10)
    || new Date().toISOString().slice(0, 10);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  // Підсумки — за весь період (сервер повертає з кожною сторінкою).
  const [totals, setTotals] = useState<VatCreditReconTotals | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<VatCreditReconRow>(
    async (limit, offset, sort) => {
      if (!effectiveOrgId) return { items: [], total: 0 };
      const resp = await taxInvoicesIncoming.creditReconciliation({
        organization_id: effectiveOrgId, date_from: effFrom, date_to: effTo,
        status: statusFilter || undefined,
        // Без кліку по заголовку — свіжі документи зверху (як раніше).
        sort_by: sort?.sort_by ?? 'doc_date',
        sort_dir: sort?.sort_dir ?? 'desc',
        limit, offset,
      });
      setTotals(resp.totals);
      return { items: resp.items, total: resp.total };
    },
    [effectiveOrgId, effFrom, effTo, statusFilter],
  );
  const t = totals;

  const cols = useMemo<ColDef<VatCreditReconRow>[]>(() => [
    {
      field: 'kind', headerName: 'Тип', width: 90,
      valueFormatter: (p) => p.data
        ? (p.value === 'receipt' ? 'прихід' : 'аванс') : '',
    },
    {
      field: 'doc_number', headerName: '№', width: 140,
      cellRenderer: (p: { data?: VatCreditReconRow }) => p.data ? (
        <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
          onClick={() => navigate(p.data!.kind === 'receipt'
            ? `/goods-receipts/${p.data!.doc_id}` : `/bank-payments/${p.data!.doc_id}`)}>
          {p.data.doc_number}
        </a>
      ) : '',
    },
    {
      field: 'doc_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    { field: 'contractor_name', headerName: 'Постачальник', flex: 1, minWidth: 200 },
    {
      field: 'amount', headerName: 'Сума', width: 120,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'expected_vat', headerName: 'ПДВ очікуваний', width: 130,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: VatCreditReconRow }) => {
        const r = p.data;
        if (!r) return '';
        return (
          <span title={r.expected_estimated
            ? 'Оцінка (сума/6): у платежі не вказано суму ПДВ' : undefined}>
            {fmt(r.expected_vat)}{r.expected_estimated && (
              <span style={{ color: 'var(--badge-amber-fg)', marginLeft: 3 }}>⚠</span>
            )}
          </span>
        );
      },
    },
    {
      // Сервер сортує за статусом (без ПН / не зареєстрована / ок).
      field: 'pn_id', headerName: 'ПН', width: 160,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: VatCreditReconRow }) => {
        const r = p.data;
        if (!r) return '';
        if (!r.pn_id) {
          return (
            <span style={{ color: r.kind === 'receipt' ? '#dc2626' : '#b45309', fontWeight: 600 }}
              title={r.kind === 'receipt'
                ? 'ПН на прихід відсутня — потягніть з ЄРПН або привʼяжіть у списку приходів'
                : 'ПН до авансу не привʼязана. Якщо оплата ПІСЛЯ приходу — ПН на приході (все гаразд); якщо це аванс (перша подія) — постачальник мав скласти ПН'}>
              ⚠ немає
            </span>
          );
        }
        const regDate = r.pn_registration_date
          ? ` ${new Date(r.pn_registration_date).toLocaleDateString('uk-UA')}` : '';
        const isErpn = r.pn_source === 'erpn';
        return (
          <a style={{ cursor: 'pointer',
            color: r.pn_registered ? '#15803d' : '#b45309',
            fontWeight: isErpn ? 600 : 400 }}
            title={`ПН №${r.pn_number}${isErpn ? ' (підтверджено ЄРПН)' : ' (вручну)'}`
              + `${r.pn_registered ? `, зареєстрована${regDate}` : ', НЕ зареєстрована — кредиту немає'}`
              + `${r.pn_posted ? ', проведена' : ', не проведена'}. Відкрити ПН`}
            onClick={() => navigate(`/tax-invoices-incoming/${r.pn_id}`)}>
            {r.pn_registered
              ? (isErpn ? `✓ ЄРПН${regDate}` : `✓ №${r.pn_number}`)
              : `⚠ №${r.pn_number} не зареєстр.`}
          </a>
        );
      },
    },
    {
      field: 'pn_vat', headerName: 'ПДВ за ПН', width: 120,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.data?.pn_id ? fmt(p.value) : '',
    },
  ], [navigate]);

  if (!hasPermissionInAnyOrg) {
    return (
      <div className="card">
        У вас немає права переглядати податкові дані у жодній з {totalAccessibleOrgs} ваших організацій.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Звірка ПДВ-кредиту<TitleTotal total={total} /></h2>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end',
        flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ margin: 0, minWidth: 240 }}>
          <label>Юрособа (платник ПДВ)</label>
          <select value={effectiveOrgId} onChange={(e) => setOrgId(e.target.value)}>
            {vatOrgs.length === 0 && <option value="">— немає платників ПДВ —</option>}
            {vatOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, width: 140 }}>
          <label>з</label>
          <DatePopover value={dateFrom} onChange={setDateFrom} />
        </div>
        <div className="field" style={{ margin: 0, width: 140 }}>
          <label>по</label>
          <DatePopover value={dateTo} onChange={setDateTo} />
        </div>
        <div className="field" style={{ margin: 0, width: 190 }}>
          <label>Показати</label>
          <select value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="">усі рядки</option>
            <option value="missing">без ПН</option>
            <option value="unregistered">ПН не зареєстрована</option>
            <option value="ok">з зареєстрованою ПН</option>
          </select>
        </div>
      </div>

      {t && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
          marginBottom: '0.75rem', fontSize: '0.88rem' }}>
          <div style={{ background: 'var(--bg-soft-2)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '0.5rem 0.75rem' }}>
            Приходи: <b>{t.receipts_count}</b> · очікуваний ПДВ{' '}
            <b>{fmt(t.receipts_expected_vat)}</b>
          </div>
          <div style={{ background: t.receipts_missing_count ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${t.receipts_missing_count ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: 8, padding: '0.5rem 0.75rem' }}>
            Без ПН (приходи): <b>{t.receipts_missing_count}</b>
            {t.receipts_missing_count > 0 && (
              <> · ПДВ під ризиком <b style={{ color: '#dc2626' }}>{fmt(t.receipts_missing_vat)}</b></>
            )}
          </div>
          <div style={{ background: 'var(--bg-soft-2)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '0.5rem 0.75rem' }}
            title="Аванс і пізніший прихід тієї самої поставки — два рядки, але кредит один. «Без ПН» по авансах — інформаційно: якщо оплата після приходу, ПН на приході.">
            Аванси: <b>{t.advances_count}</b> · без ПН <b>{t.advances_missing_count}</b>
            {t.advances_missing_count > 0 && <> (~{fmt(t.advances_missing_vat)} ПДВ)</>}
          </div>
          <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
            borderRadius: 8, padding: '0.5rem 0.75rem' }}>
            ПДВ за привʼязаними ПН: <b>{fmt(t.pn_vat_total)}</b>
            {t.pn_unregistered_count > 0 && (
              <> · <span style={{ color: 'var(--badge-amber-fg)' }}>
                не зареєстровано ПН: <b>{t.pn_unregistered_count}</b>
              </span></>
            )}
          </div>
        </div>
      )}

      <InfiniteDocGrid<VatCreditReconRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="vat-credit-recon"
      />
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.5rem' }}>
        Проведені приходи та аванси (оплати постачальникам) від платників ПДВ.
        Потягнути ПН з ЄРПН: Кабінет ДПС → «Отримати ПН». Привʼязати вручну —
        у списках приходів/платежів (колонка «ПН»).
      </p>
    </div>
  );
}
